import { Pagination } from "types/Models/pagination";
import { emptyApi } from "./emptyApi";
import { AUDIT_LOG_KEYS, AuditLogKey } from "constants/auditlogKeys";
import { AuditLogAction } from "constants/auditlogActions";
import { AuditLogContentType } from "constants/auditLogFilters";

export type AuditLog = {
  uuid: string;
  frontend_url: string | null;
  product_type:
    | "TAX"
    | "BOOKS"
    | "SALES_TAX"
    | "PLATFORM"
    | "MAILROOM"
    | "INTRAGROUP"
    | "BOARD"
    | "TAX_CREDITS"
    | "COMMUNITY";
  company_group?: {
    uuid: string;
    name: string;
  };
  profile?: {
    uuid: string;
    name: string;
    short_name: string;
  };
  company_entity?: {
    uuid: string;
    name: string;
  };
  obj?: {
    uuid: string;
    content_type: AuditLogContentType;
    description?: string;
  };
  event_type?: string;
  event: {
    name: string;
    description: string;
    time: string;
  };
  audited_changes: Record<
    AuditLogKey,
    {
      after: { uuid: string; value: string };
      before: { uuid: string; value: string };
      data_type?: "date";
    } | null
  >;
  action: AuditLogAction;
  owner: {
    id: string;
    name: string;
    user_type: string;
  };
  created_at?: string;
  resource: string;
  resource_details?: {
    uuid: string;
    ledger_entry_serial_number: string;
    ledger_entry_uuid: string;
  };
};

export const auditlogs = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getCompanyGroupAuditLogs: build.query<
      Pagination & { logs: AuditLog[] },
      {
        groupId?: string;
        entityId?: string;
        productTypes?: string;
        profileIdList?: string;
        startDate?: string;
        endDate?: string;
        pageNum?: number;
        searchTerm?: string;
        source?: string;
        features?: string;
        pageSize?: number;
      }
    >({
      query: ({
        groupId,
        entityId,
        productTypes,
        profileIdList,
        startDate,
        endDate,
        pageNum,
        searchTerm,
        source,
        features,
        pageSize,
      }) => {
        return {
          url: `/api/inkle/auditlogs/company_group/${groupId}/logs/`,
          params: {
            group_id: groupId,
            entity_id: entityId,
            product_types: productTypes,
            profile_id_list: profileIdList,
            start_date: startDate,
            end_date: endDate,
            page_num: pageNum,
            search_term: searchTerm,
            source,
            features,
            page_size: pageSize,
          },
        };
      },
    }),
    getAuditLogs: build.query<
      Pagination & { logs: AuditLog[] },
      {
        groupIds?: string;
        entityId?: string;
        productTypes?: string;
        profileIdList?: string;
        startDate?: string;
        endDate?: string;
        pageNum?: number;
        searchTerm?: string;
        features?: string;
        source?: string;
        pageSize?: number;
      }
    >({
      query: ({
        groupIds,
        entityId,
        productTypes,
        profileIdList,
        startDate,
        endDate,
        pageNum,
        searchTerm,
        source,
        features,
        pageSize,
      }) => {
        return {
          url: `/api/inkle/auditlogs/logs/`,
          params: {
            group_ids: groupIds,
            entity_id: entityId,
            product_types: productTypes,
            profile_id_list: profileIdList,
            start_date: startDate,
            end_date: endDate,
            page_num: pageNum,
            search_term: searchTerm,
            source,
            features,
            page_size: pageSize,
          },
        };
      },
    }),

    getByObjectIdAuditLogs: build.query<
      Pagination & { logs: AuditLog[] },
      {
        entityId: string;
        objectId: string;
        page_num?: number;
      }
    >({
      query: ({ entityId, objectId, page_num }) => {
        return {
          url: `/api/inkle/auditlogs/entity/${entityId}/object/${objectId}/logs/`,
          params: {
            entity_id: entityId,
            page_num,
          },
        };
      },
      providesTags: (result) => (result ? ["AUDIT_LOG_BY_OBJECT"] : []),
    }),

    getInfiniteByObjectIdAuditLogs: build.infiniteQuery<
      Pagination & { logs: AuditLog[] },
      {
        entityId: string;
        objectId: string;
      },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        maxPages: 5,
        getNextPageParam: (
          lastPage,
          allPages,
          lastPageParam,
          allPageParams,
          queryArg
        ) => {
          if (lastPageParam < (lastPage.total_pages || 1)) {
            return lastPageParam + 1;
          }
          return undefined;
        },
        getPreviousPageParam: (
          firstPage,
          allPages,
          firstPageParam,
          allPageParams,
          queryArg
        ) => {
          return firstPageParam > 1 ? firstPageParam - 1 : undefined;
        },
      },
      query({ queryArg, pageParam }) {
        const { entityId, objectId } = queryArg;
        return {
          url: `/api/inkle/auditlogs/entity/${entityId}/object/${objectId}/logs/`,
          params: {
            entity_id: entityId,
            page_num: pageParam,
          },
        };
      },
      providesTags: (result) => (result ? ["AUDIT_LOG_BY_OBJECT"] : []),
    }),
  }),
});

export const {
  useGetAuditLogsQuery,
  useGetCompanyGroupAuditLogsQuery,
  useGetByObjectIdAuditLogsQuery,
  useLazyGetByObjectIdAuditLogsQuery,
  useGetInfiniteByObjectIdAuditLogsInfiniteQuery,
} = auditlogs;
